import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { STEPS, buildPayload } from "../components/empieza/Steps";
import "../components/empieza/empieza.css";

const QV_LOGO = "https://customer-assets.emergentagent.com/job_athletic-edge-19/artifacts/7jyn9i1w_IMG_2453.PNG";
const KEY = "qv_empieza_v1";
const EMPTY = {
  filled_by: "", player_name: "", guardian_name: "", age: null, positions: [], goals: [], goals_detail: "",
  current_situation: "", obstacle: "", budget: "", start_when: "", prefix: "+34", phone: "", email: "", consent: false, website: "",
};

const load = () => {
  try { const s = JSON.parse(sessionStorage.getItem(KEY)); if (s?.a) return { ...s, a: { ...EMPTY, ...s.a } }; } catch { /* noop */ }
  return { a: EMPTY, step: -1 };
};

const Background = () => (
  <div className="emp-bg" aria-hidden="true">
    <img src={QV_LOGO} alt="" className="emp-logo-big" />
    <img src={QV_LOGO} alt="" className="emp-logo-small" />
  </div>
);

const Intro = ({ onStart }) => (
  <div className="emp-done" data-testid="empieza-intro">
    <div className="qv-overline">QV Performance · Solicitud</div>
    <h1 className="emp-q" style={{ fontSize: "clamp(2.8rem, 12vw, 4.2rem)" }}>
      Cuéntame tu<br /><span style={{ color: "var(--qv-red)" }}>situación.</span>
    </h1>
    <p className="emp-help" style={{ fontSize: 16, maxWidth: 420 }}>
      Unas preguntas rápidas sobre el futbolista y su objetivo. Tardas unos 3 minutos y puedes volver atrás cuando quieras.
    </p>
    <button className="emp-btn" style={{ flex: "none", marginTop: 12 }} onClick={onStart} data-testid="empieza-start">
      Empezar <ArrowRight size={18} />
    </button>
  </div>
);

const Done = () => (
  <div className="emp-done" data-testid="empieza-success">
    <div className="emp-done-icon"><Check size={38} strokeWidth={3} /></div>
    <h1 className="emp-q">¡Formulario recibido!</h1>
    <p className="emp-help" style={{ fontSize: 16, color: "#d4d4d4" }}>
      Gracias por contarme tu situación. Revisaré tus respuestas y, si tu caso encaja con el servicio, me pondré en contacto contigo para explicarte cómo podemos trabajar.
    </p>
  </div>
);

export default function Empieza() {
  const init = useRef(load()).current;
  const [a, setA] = useState(init.a);
  const [step, setStep] = useState(init.step);
  const [dir, setDir] = useState("fwd");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const timer = useRef(null);

  useEffect(() => { document.title = "Empieza · QV Performance"; }, []);
  useEffect(() => { if (!done) sessionStorage.setItem(KEY, JSON.stringify({ a, step })); }, [a, step, done]);
  useEffect(() => { window.scrollTo({ top: 0 }); }, [step]);

  const total = STEPS.length;
  const cur = STEPS[step];
  const go = (n) => { clearTimeout(timer.current); setDir(n > step ? "fwd" : "back"); setStep(n); };

  const set = (patch) => {
    const next = { ...a, ...patch };
    setA(next);
    if (cur?.auto && cur.valid(next) && step < total - 1) timer.current = setTimeout(() => go(step + 1), 280);
  };

  const submit = async () => {
    setSending(true);
    try {
      await api.post("/captacion", buildPayload(a));
      sessionStorage.removeItem(KEY);
      setDone(true);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSending(false);
    }
  };

  const last = step === total - 1;
  const valid = cur ? cur.valid(a) : false;

  return (
    <div className="emp-root" data-testid="empieza-page">
      <Background />
      <div className="emp-wrap">
        <div className="emp-head">
          <div className="emp-brand">
            <img src={QV_LOGO} alt="QV Performance" />
            <div className="qv-display" style={{ fontSize: 18, letterSpacing: "0.04em" }}>QV PERFORMANCE</div>
          </div>
        </div>

        {done ? <Done /> : step < 0 ? <Intro onStart={() => go(0)} /> : (
          <>
            <div className="emp-progress" data-testid="empieza-progress"><div style={{ width: `${((step + 1) / total) * 100}%` }} /></div>
            <div className="emp-step-count" data-testid="empieza-step-count">Paso {step + 1} de {total}</div>
            <div key={step} className={`emp-body${dir === "back" ? " back" : ""}`}>{cur.render(a, set)}</div>
            <input type="text" tabIndex={-1} autoComplete="off" value={a.website} onChange={(e) => setA({ ...a, website: e.target.value })}
              style={{ position: "absolute", left: -9999, width: 1, height: 1, opacity: 0 }} aria-hidden="true" name="website" />
            <div className="emp-nav">
              <button type="button" className="emp-btn ghost" onClick={() => go(step - 1)} aria-label="Atrás" data-testid="empieza-back">
                <ArrowLeft size={20} />
              </button>
              <button type="button" className="emp-btn" disabled={!valid || sending}
                onClick={last ? submit : () => go(step + 1)} data-testid={last ? "empieza-submit" : "empieza-next"}>
                {last ? (sending ? "Enviando…" : "Enviar solicitud") : "Siguiente"} {!sending && <ArrowRight size={18} />}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
